import { create } from 'zustand';
import { gameAPI } from '../services/api';
import i18n from '../i18n';
import SoundManager from '../services/SoundManager';

interface User {
  id: number;
  telegramId: string;
  telegram_id?: string; // Для совместимости
  username: string;
  balance: number;
  totalEarned: number;
  rank: string;
  vipLevel: number;
  lastClaim: Date | null;
  dailyEarnings?: number;
  referrals?: number;
  referralBonus?: number;
  tasksDone?: number;
  miningRate?: number;
  totalAPR?: number;
  referralCount?: number;
  referralCode?: string;
  total_staked?: number;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  totalClaims?: number;
  claimStreak?: number;
  vipBonusApr?: number;
  createdAt?: string | Date;
}

interface AppState {
  // Пользователь
  user: User | null;
  isAuthenticated: boolean;
  
  // Курсы валют
  tonPrice: number;
  spermPrice: number;
  
  // Настройки
  graphicsQuality: 'low' | 'medium' | 'high';
  language: 'ru' | 'en';
  soundEnabled: boolean;
  musicEnabled: boolean;
  selectedMusic: string;

  // Методы
  initApp: () => Promise<void>;
  setUser: (user: User) => void;
  setGraphicsQuality: (quality: 'low' | 'medium' | 'high') => void;
  setLanguage: (lang: 'ru' | 'en') => void;
  setSoundEnabled: (enabled: boolean) => void;
  setMusicEnabled: (enabled: boolean) => void;
  setSelectedMusic: (track: string) => void;
  fetchPrices: () => Promise<void>;
  updateBalance: (newBalance: number) => void;
}

// Конфигурация API уже в services/api.ts

export const useStore = create<AppState>((set, get) => ({
  // Начальное состояние
  user: null,
  isAuthenticated: false,
  tonPrice: 3.20,
  spermPrice: 0.036,
  graphicsQuality: (localStorage.getItem('graphicsQuality') as 'low' | 'medium' | 'high') || 'high',
  language: (localStorage.getItem('language') as 'ru' | 'en') || 'ru',
  soundEnabled: localStorage.getItem('soundEnabled') !== 'false', // По умолчанию включен
  musicEnabled: localStorage.getItem('musicEnabled') !== 'false', // По умолчанию включена
  selectedMusic: localStorage.getItem('selectedMusic') || 'lunar_landing', // По умолчанию Lunar Landing Lounge
  
  // Инициализация приложения
  initApp: async () => {
    console.log('🎮 useStore.initApp called');
    try {
      // Получаем настройки графики из localStorage
      const savedQuality = localStorage.getItem('graphicsQuality') as 'low' | 'medium' | 'high';
      if (savedQuality) {
        set({ graphicsQuality: savedQuality });
      }

      // Получаем данные пользователя из Telegram WebApp
      if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
        const tgUser = window.Telegram.WebApp.initDataUnsafe.user;
        console.log('✅ Telegram WebApp detected, user:', tgUser.username);

        try {
          // 🔧 Проверяем devmode в URL
          const urlParams = new URLSearchParams(window.location.search);
          const isDevMode = urlParams.get('devmode') === 'true';

          let response;

          if (isDevMode) {
            // DEV MODE: Авторизация по telegram_id (без проверки подписи)
            console.log('🔧 DEV MODE: Авторизация через /auth/dev, ID:', tgUser.id);
            response = await gameAPI.authDev(tgUser.id.toString());
          } else {
            // PRODUCTION: Используем РЕАЛЬНЫЙ initData от Telegram
            const initData = window.Telegram.WebApp.initData;

            if (!initData || initData.trim() === '') {
              console.error('❌ Telegram initData отсутствует или пустой');
              console.log('⚠️ Приложение продолжит работу без авторизации');
              // НЕ возвращаемся - продолжаем загрузку приложения
              return;
            }

            console.log('🔐 Авторизация через Telegram, ID:', tgUser.id);

            // Отправляем на сервер для авторизации
            response = await gameAPI.authTelegram(initData, tgUser.id);
          }

          if (response.data.success) {
            // Сохраняем токен
            if (response.data.token) {
              localStorage.setItem('token', response.data.token);
            }

            // Используем данные пользователя из ответа
            const userData = response.data.user;
            const user: User = {
              id: userData.id,
              telegramId: userData.telegramId,
              username: userData.username || tgUser.username,
              balance: userData.balance || 0,
              totalEarned: userData.totalEarned || 0,
              rank: userData.rank || 'No Rank',
              vipLevel: userData.vipLevel || 0,
              lastClaim: userData.lastClaim ? new Date(userData.lastClaim) : null,
              dailyEarnings: userData.dailyEarnings || 0,
              referralCount: userData.referralCount || 0,
              referralBonus: userData.referralBonus || 0,
              tasksDone: userData.tasksDone || 0,
              miningRate: userData.miningRate || 83.3,
              totalAPR: userData.totalAPR || 0,
              vipBonusApr: userData.vipBonusApr || 0,
              firstName: userData.firstName || tgUser.first_name,
              lastName: userData.lastName || tgUser.last_name,
              referralCode: userData.referralCode || userData.referral_code,
              photoUrl: userData.photoUrl || userData.photo_url
            };

            // Устанавливаем звук - приоритет у локальных настроек
            const localSoundEnabled = localStorage.getItem('soundEnabled');
            const soundEnabled = localSoundEnabled !== null ? localSoundEnabled === 'true' : (userData.soundEnabled !== false);
            set({ soundEnabled });
            localStorage.setItem('soundEnabled', soundEnabled ? 'true' : 'false');
            SoundManager.setSoundEnabled(soundEnabled);

            // Устанавливаем язык из БД (ВСЕГДА применяем при авторизации!)
            const language = userData.languageCode === 'ru' ? 'ru' : 'en';
            console.log('🌐 Язык из БД:', userData.languageCode, '→', language, '| Текущий в store:', get().language);
            get().setLanguage(language); // Всегда синхронизируем с БД

            console.log('✅ Пользователь авторизован:', user.username, 'Photo:', user.photoUrl ? 'есть' : 'нет', 'Sound:', soundEnabled, 'Lang:', language);
            set({ user, isAuthenticated: true });
          } else if (response.data.needRegistration) {
            // Пользователь не зарегистрирован
            console.warn('⚠️ Пользователь не найден в БД. Нужно написать боту /start');
            alert('Пожалуйста, сначала напишите боту /start в Telegram');
          }
        } catch (error) {
          console.error('❌ Ошибка авторизации:', error);
        }
      }

      // Загружаем курсы валют (не блокируем если не получится)
      get().fetchPrices().catch(console.error);

    } catch (error) {
      console.error('❌ Ошибка инициализации:', error);
    }
  },
  
  // Установка пользователя
  setUser: (user) => set({ user, isAuthenticated: true }),
  
  // Установка качества графики
  setGraphicsQuality: (quality) => {
    set({ graphicsQuality: quality });
    localStorage.setItem('graphicsQuality', quality);
    
    // Применяем класс к корневому элементу
    document.querySelector('.app')?.setAttribute('data-quality', quality);
  },
  
  // Установка языка
  setLanguage: (lang) => {
    set({ language: lang });
    localStorage.setItem('language', lang);

    // Синхронизация с i18next
    i18n.changeLanguage(lang);

    // Применяем язык к корневому элементу для стилей
    document.querySelector('.app')?.setAttribute('data-lang', lang);

    // Синхронизация с БД (для уведомлений бота)
    const token = localStorage.getItem('token');
    if (token) {
      // Динамический импорт для избежания циклических зависимостей
      import('../services/api').then(({ gameAPI }) => {
        gameAPI.updateLanguage(lang).catch((err) => {
          console.error('Ошибка синхронизации языка с сервером:', err);
        });
      });
    }
  },
  
  // Установка звука
  setSoundEnabled: (enabled) => {
    set({ soundEnabled: enabled });
    localStorage.setItem('soundEnabled', enabled ? 'true' : 'false');
    // Применяем настройку к SoundManager
    SoundManager.setSoundEnabled(enabled);
  },

  // Установка музыки
  setMusicEnabled: (enabled) => {
    set({ musicEnabled: enabled });
    localStorage.setItem('musicEnabled', enabled ? 'true' : 'false');
    // Применяем настройку к SoundManager
    SoundManager.setMusicEnabled(enabled);
  },

  // Установка выбранной музыки
  setSelectedMusic: (track) => {
    set({ selectedMusic: track });
    localStorage.setItem('selectedMusic', track);
  },
  
  // Получение курсов валют
  fetchPrices: async () => {
    try {
      // Получаем курсы обмена с сервера (через STON.fi)
      const response = await gameAPI.getExchangeRate();
      if (response.data?.success && response.data?.rate) {
        const rate = response.data.rate;
        set({ 
          tonPrice: rate.tonPriceUSD || 3.20,
          spermPrice: rate.spermPriceUSD || 0.036
        });
        console.log('Курсы обновлены:', { 
          ton: rate.tonPriceUSD, 
          sperm: rate.spermPriceUSD,
          source: rate.source 
        });
      } else if (response.data) {
        // Fallback для старого формата
        set({ 
          tonPrice: response.data.tonPriceUSD || 3.20,
          spermPrice: response.data.spermPriceUSD || 0.036
        });
      }
    } catch (error) {
      console.error('Ошибка получения курсов:', error);
      // Используем значения по умолчанию
      set({ 
        tonPrice: 3.20,
        spermPrice: 0.036
      });
    }
  },
  
  // Обновление баланса
  updateBalance: (newBalance) => {
    set((state) => ({
      user: state.user ? { ...state.user, balance: newBalance } : null
    }));
  }
}));

// Типы для TypeScript
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
          };
        };
        ready: () => void;
        expand: () => void;
        close: () => void;
        setHeaderColor: (color: string) => void;
        setBackgroundColor: (color: string) => void;
        openLink: (url: string) => void;
        openTelegramLink: (url: string) => void;
      };
    };
  }
}